import { useEffect } from 'react';

import { useSearchParams } from '@remix-run/react';

import addBackPlusSignToEmail from '~/utils/addBackPlusSignToEmail';
import isValidEmail from '~/utils/isValidEmail';

import useLocalStorage from './useLocalStorage';

// TODO: Come back to the is isValid function here. It was causing issues with emails that included a + in them.
// But that behavior is incorrect, those are valid emails. Currently, we aren't using this value outside of the redirect flow anyways.

// function isValid(email: string) {
//   return /^\S+@\S+\.\S+$/.test(email);
// }

/**
 * Enables accessing an email address from either the URL or localStorage. Favors the
 * search param over localStorage and will update localStorage if it doesn't match the
 * search param. If the email is not valid, its removed in both places.
 *
 * @note This intentionally does not set the search param on update, but will keep localStorage
 *       up to date. This means that a refresh could reset localStorage to the search param.
 */
// One-off read for components that mount before the email is stored (useEmail only reads at mount).
export const readStoredEmail = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const stored = JSON.parse(window.localStorage.getItem('email') ?? '""');
    const email = addBackPlusSignToEmail(typeof stored === 'string' ? stored : '');
    return isValidEmail(email) ? email : undefined;
  } catch {
    return undefined;
  }
};

const useEmail = (): [string | undefined, (email: string) => void] => {
  const [searchParams] = useSearchParams();

  const [localEmail, setLocalEmail] = useLocalStorage('email', '');
  const searchEmail = searchParams.get('email');

  const email = searchEmail || localEmail;
  const setEmail = (email: string) => {
    setLocalEmail(email);
  };

  useEffect(() => {
    if (searchEmail && !localEmail) {
      setLocalEmail(searchEmail);
    }

    if (searchEmail && localEmail && searchEmail !== localEmail) {
      setLocalEmail(searchEmail);
    }
  }, []);

  const modifiedEmail = addBackPlusSignToEmail(email);

  return [isValidEmail(modifiedEmail) ? modifiedEmail : undefined, setEmail];
};

export default useEmail;
